import { CARD_H, CARD_W } from "./constants.js";
import { resizeArea, toU8 } from "./image.js";
import { grayPlane } from "./signature.js";

/* ===========================================================================
   Sleeved cards: detect and trim the sleeve ring inside a canonical warp.

   A card in a sleeve defeats the corner detector whenever the card's edge has less
   contrast against the sleeve's backing than the sleeve has against the table — a pale
   card in a white-backed sleeve, any black-bordered card in a black-backed one. The
   detector then quads the *sleeve*, and the 488x680 warp contains the card plus a
   uniform ring. Every downstream region is offset by that ring: the art box clips
   frame (ORB collapses to zero inliers), the embedder reads the ring as a border
   colour (a white ring retrieves white-bordered cards), and the OCR crops miss their
   lines. Measured live: Marsh Flats spg 110 in a white-backed sleeve retrieved its own
   row at d=12.4 and was still REJECTED at 0.26.

   The ring, however, is trivial to see in the warp itself: an axis-aligned band of
   near-uniform colour along each edge. This module measures it and produces a trimmed
   re-warp. Callers use it as a *retry* after a REJECTED read — a retry can only
   replace a rejection with something better, so an over-eager trim costs nothing.
   =========================================================================== */

//: Widest ring worth believing, in canonical pixels — ~13% of the card's width. Loose
//: sleeves measured wider than the first 10% cap (a live band pinned at exactly 48 px
//: meant "wider than the cap", not "48"); the centre-texture gate keeps blank scenes
//: from riding this generosity.
const MAX_RING = 64;
//: Bands thinner than this are the card's own edge print, not a ring.
const MIN_RING = 5;
//: A ring line is uniform: little texture along it, and no jump from its neighbour.
const MAX_LINE_STDDEV = 18;
const MAX_NEIGHBOUR_DELTA = 6;

/** Mean and stddev of one line of the ring scan, sampled over the central 70%. */
function lineStats(gray, w, h, side, offset) {
  const horizontal = side === "top" || side === "bottom";
  const length = horizontal ? w : h;
  const lo = Math.floor(length * 0.15);
  const hi = Math.ceil(length * 0.85);
  let sum = 0;
  let squares = 0;
  for (let i = lo; i < hi; i++) {
    let value;
    if (side === "top") value = gray[offset * w + i];
    else if (side === "bottom") value = gray[(h - 1 - offset) * w + i];
    else if (side === "left") value = gray[i * w + offset];
    else value = gray[i * w + (w - 1 - offset)];
    sum += value;
    squares += value * value;
  }
  const n = hi - lo;
  const mean = sum / n;
  return { mean, stddev: Math.sqrt(Math.max(0, squares / n - mean * mean)) };
}

//: A warp whose centre carries less texture than this is blank scene — a table
//: surface, not a sleeved card. Card art and text sit in the centre and measure far
//: above it.
const MIN_CENTRE_STDDEV = 16;

/**
 * Measure the sleeve evidence of a canonical warp.
 *
 * `bands` is the raw uniform band along each edge, always reported so a caller can
 * log why nothing was trimmed. `ring` is the conclusion: null when fewer than two
 * sides carry a band (a card sits flush against one sleeve edge routinely, so one
 * missing side is normal) or when the centre is as featureless as the edges — then
 * the warp is scene, not card, and trimming it would only resample noise.
 */
export function inspectSleeve(card) {
  const gray = grayPlane(card, CARD_W, CARD_H, 0, 0, CARD_W, CARD_H);
  const bands = { left: 0, top: 0, right: 0, bottom: 0 };
  for (const side of ["top", "bottom", "left", "right"]) {
    let previous = null;
    let band = 0;
    for (let offset = 0; offset < MAX_RING; offset++) {
      const { mean, stddev } = lineStats(gray, CARD_W, CARD_H, side, offset);
      if (stddev > MAX_LINE_STDDEV) break;
      if (previous != null && Math.abs(mean - previous) > MAX_NEIGHBOUR_DELTA) break;
      previous = mean;
      band++;
    }
    bands[side] = band >= MIN_RING ? band : 0;
  }

  // Texture of the central 60% — coarsely sampled, this is a gate, not a measurement.
  let sum = 0;
  let squares = 0;
  let count = 0;
  for (let y = Math.floor(CARD_H * 0.2); y < CARD_H * 0.8; y += 4) {
    for (let x = Math.floor(CARD_W * 0.2); x < CARD_W * 0.8; x += 4) {
      const value = gray[y * CARD_W + x];
      sum += value;
      squares += value * value;
      count++;
    }
  }
  const mean = sum / count;
  const centre = Math.sqrt(Math.max(0, squares / count - mean * mean));

  const sides = [bands.left, bands.top, bands.right, bands.bottom].filter(
    (band) => band >= MIN_RING,
  ).length;
  const ring = sides >= 2 && centre >= MIN_CENTRE_STDDEV ? bands : null;
  return { ring, bands, centre };
}

/** The conclusion alone — see {@link inspectSleeve}. */
export function detectSleeveRing(card) {
  return inspectSleeve(card).ring;
}

/**
 * The warp with the ring cut away, resampled back to 488x680.
 *
 * A second resample costs a little sharpness, which is why this is a retry rather than
 * a default. Null when the trim would leave less than a plausible card.
 */
export function trimRing(card, ring) {
  const x0 = ring.left;
  const y0 = ring.top;
  const w = CARD_W - ring.left - ring.right;
  const h = CARD_H - ring.top - ring.bottom;
  if (w < CARD_W * 0.75 || h < CARD_H * 0.75) return null;

  const sub = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y++) {
    const from = ((y + y0) * CARD_W + x0) * 4;
    sub.set(card.subarray(from, from + w * 4), y * w * 4);
  }
  return new Uint8ClampedArray(toU8(resizeArea(sub, w, h, CARD_W, CARD_H, 4)));
}
